// Third party library imports
import { NgIf } from '@angular/common';
import {
    Component,
    inject,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormsModule,
    NgForm,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterLink } from '@angular/router';
import { Guid } from 'guid-typescript';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { Subject } from 'rxjs';

// Zopup library imports
import { CountryStore, UserStore } from '@zopup/zop-apis';
import {
    emailValidator,
    EnumToDescriptionPipe,
    phoneValidator,
    StorageService,
} from '@zopup/zop-shared';
import {
    CountryDto,
    EmailAddressDto,
    PhoneNumberDto,
    Salutation,
    UserCreateCommand,
    UserDto,
    UserEmailInvitationCommand,
} from '@zopup/zop-types';
import {
    ZopAlertComponent,
    ZopAlertType,
    zopAnimations,
} from '@zopup/zop-ux-core';

// Internal project imports
import { AuthService } from '../../services/auth/auth.service';
import { SharedModule } from '../shared.module';
import { ZopAuthConfigService } from '../../providers/zop-auth-config.provider';

@Component({
    selector: 'zop-auth-sign-up',
    templateUrl: './sign-up.component.html',
    encapsulation: ViewEncapsulation.None,
    animations: zopAnimations,
    imports: [
        RouterLink,
        NgIf,
        ZopAlertComponent,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
        SharedModule,
        MatLuxonDateModule,
        EnumToDescriptionPipe,
        LottieComponent,
    ]
})
export class AuthSignUpComponent implements OnInit, OnDestroy {

    readonly countryStore = inject(CountryStore);
    readonly userStore = inject(UserStore);
    readonly authConfig = inject(ZopAuthConfigService);
    @ViewChild('signUpNgForm') signUpNgForm: NgForm;

    options: AnimationOptions = {
        path: '/assets/animation/zopup-dark.json',
        autoplay: true,
        loop: false,
    };

    alert: { type: ZopAlertType; message: string } = {
        type: 'success',
        message: '',
    };
    signUpForm: UntypedFormGroup;
    showAlert: boolean = false;
    salutationArray: Salutation[] = [];
    countries: CountryDto[];

    public maxDate: Date = new Date();
    public filteredCountriesMobile: CountryDto[];
    public filteredCountriesPhone: CountryDto[];

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    /**
     * Constructor
     */
    constructor(
        private _formBuilder: UntypedFormBuilder,
        private _router: Router,
        private _authService: AuthService,
        private _storageService: StorageService,
    ) {
        this.salutationArray = Object.values(Salutation).filter(
            (value) => value !== Salutation.NA
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    async ngOnInit(): Promise<void> {
        this._storageService.clearStorage();

        await this.countryStore.getList();
        this.countries = this.countryStore.countries();
        this.filteredCountriesMobile = this.countries.slice();
        this.filteredCountriesPhone = this.countries.slice();
        
        // Create the form
        this.createForm();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    createForm(): void {
        this.signUpForm = this._formBuilder.group({
            salutation: [Salutation.Mr, Validators.required],
            firstName: [
                '',
                [
                    Validators.required,
                    Validators.minLength(1),
                    Validators.maxLength(50),
                ],
            ],
            lastName: ['', [Validators.minLength(1), Validators.maxLength(50)]],
            email: [
                '',
                [
                    Validators.required,
                    Validators.minLength(1),
                    Validators.maxLength(250),
                    emailValidator(),
                ],
            ],
            password: ['', [Validators.required, Validators.minLength(6)]],
            mobileNumber: ['', [Validators.required, phoneValidator()]],
            mobileNumberCountry: ['in'],
            agreements: ['', Validators.requiredTrue],
        });
    }

    /**
     * Sign up
     */
    signUp(): void {

        try {
            // Do nothing if the form is invalid
            if (this.signUpForm.invalid) {
                return;
            }

            // Disable the form
            this.signUpForm.disable();

            // Hide the alert
            this.showAlert = false;

            // Sign up
            this._authService.signUp(this.signUpForm.value).then(
                (response: any) => {
                    if (response.isSuccess) {
                        this.createUser(response.result);
                    } else {
                        if (response.errors.length > 0) {
                            if (response.errors[0].message.indexOf('email-already-in-use') > -1) {
                                this.displayError('Email already registered. Please Sign-in!');
                            } else if (response.errors[0].message.indexOf('invalid-email') > -1) {
                                this.displayError('Invalid email!');
                            } else if (response.errors[0].message.indexOf('WEAK_PASSWORD') > -1) {
                                this.displayError('Password should be at least 6 characters!');
                            }
                            
                        }
                    }
                },
                (response) => {
                    // Reset the form
                    this.signUpNgForm.resetForm();
                    
                    this.displayError('Something went wrong, please try again.');
                }
            );
        } catch(ex) {
            console.error('Exception in sign-up.component => signUp()');
            console.error(ex);
        } finally {
            // Enable the form
            this.signUpForm.enable();
        }
    }

    async createUser(firebaseResponse: any): Promise<void> {
        try {
            const userCommand = new UserCreateCommand();
            userCommand.userDetails = new UserDto();
            userCommand.userDetails.uId = firebaseResponse.uid;

            userCommand.userDetails.email = new EmailAddressDto();
            userCommand.userDetails.email.label = 'Primary';
            userCommand.userDetails.email.value = firebaseResponse.email;

            // userCommand.userDetails.createdTime = moment(firebaseResponse.metadata.creationTime).toDate();
            userCommand.userDetails.emailVerified = firebaseResponse.emailVerified;
            userCommand.userDetails.firstName = this.signUpForm.get('firstName')!.value;
            userCommand.userDetails.lastName = this.signUpForm.get('lastName')!.value;
            userCommand.userDetails.displayName = userCommand.userDetails.firstName + ' ' + userCommand.userDetails.lastName;

            userCommand.userDetails.mobileNumber = new PhoneNumberDto();
            userCommand.userDetails.mobileNumber.label = 'Primary';
            // userCommand.userDetails.mobileNumber.country = this.getCountryByIso(this.signUpForm.get('mobileNumberCountry')!.value).name;
            userCommand.userDetails.mobileNumber.countryCode = this.getCountryByIso(this.signUpForm.get('mobileNumberCountry')!.value).code;
            userCommand.userDetails.mobileNumber.value =
                this.getCountryByIso(this.signUpForm.get('mobileNumberCountry')!.value).code + this.signUpForm.get('mobileNumber')!.value;

            userCommand.userDetails.photoUrl = firebaseResponse.metadata.photoURL;
            userCommand.userDetails.disabled = false;
            userCommand.userDetails.tenants = [];

            const response = await this.userStore.add(userCommand);
            if (!response.isSuccess || !response.result) {
                console.error('Error in creating the user!');
                return;
            }
            this._storageService.user = response.result;

            // Send email invitation
            const command = new UserEmailInvitationCommand();
            command.id = response.result.id;

            const emailInvitationResponse = await this.userStore.sendEmailInvitation(command);
            if (
                emailInvitationResponse &&
                emailInvitationResponse.isSuccess &&
                emailInvitationResponse.result
            ) {
                this._router.navigateByUrl('/confirmation-required');
            } else {
                this.displayError('Unable to send email invitation. Please contact admin!');
            }
        } catch (ex) {
            console.error('Exception in sign-up.component => createUser()');
            console.error(ex);
        }
    }

    displayError(errorMsg: string): void {
        // Set the alert
        this.alert = {
            type: 'error',
            message: errorMsg,
        };

        // Display the alert
        this.showAlert = true;
    }

    /**
     * Get country info by iso code
     *
     * @param iso
     */
    getCountryByIso(code: string): CountryDto {
        return this.countries.find((country) => country.code === code);
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }
}
